/**
 * Problem reports — the contract between the surfaces that let a person say
 * "this went wrong" and the API's append-only report tables (migration 021).
 * APP-MONITORING M2.1, M2.3–M2.8; increment-11 plan §2 K-PR5.
 *
 * The load-bearing facts, fixed here so no surface can drift on them:
 *   - Report ids are DEVICE-minted UUIDv7 (ids.ts). A report written in
 *     airplane mode survives the app being killed and is delivered later;
 *     replay is idempotent by INSERT on the id, so a retrying queue lands one
 *     row however many times it delivers — the tap queue's discipline exactly.
 *   - `occurredAt` is the REPORTER's clock — the fact. The server records its
 *     own receipt time beside it, never over it, so a report syncing six hours
 *     later still says when the person hit submit (ADR-0010 §3).
 *   - The reference the reporter is shown is DERIVED from the id, never stored
 *     beside it. A stored copy of a derived fact is a second truth that can
 *     drift (018's rule); `reportReference` is the one place it is computed,
 *     so the app, the API and the runbook all say the same string.
 *   - Free text is PHI-capable BY ASSUMPTION. It rides the `report` sink of
 *     the scrubber (the only sink that accepts prose), is excluded from every
 *     study export, and is NEVER forwarded to Sentry. A report points AT a
 *     crash by event id; a crash never points back.
 */

import { z } from 'zod';

/**
 * What broke, from the reporter's point of view — the shapes a physio can
 * actually distinguish mid-session, not the layers an engineer would name.
 * Mirrored by migration 021's CHECK; the schema and this list must move
 * together (004's tap-kind relationship).
 */
export const REPORT_CATEGORIES = [
  'video-audio',
  'recording',
  'pose-tracking',
  'taps',
  'sign-off',
  'sign-in',
  'performance',
  'something-else',
] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

/**
 * How bad it was FOR THE PERSON. Deliberately three words and not a 1–5 scale:
 * the question is whether they could carry on, and a scale invites a precision
 * nobody has mid-call. The operator's own triage grade is a later event row,
 * never a rewrite of what the reporter said.
 */
export const REPORT_SEVERITIES = ['blocked', 'degraded', 'annoyance'] as const;
export type ReportSeverity = (typeof REPORT_SEVERITIES)[number];

/**
 * The lifecycle vocabulary. Ordered by intent, NOT enforced as a state machine
 * — migration 021 says why: an operator who reopens a closed report by
 * appending `in-progress` is recording what happened, and a constraint calling
 * that impossible would only teach people to lie to it.
 */
export const REPORT_STATUSES = ['new', 'triaged', 'in-progress', 'resolved', 'closed'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

/**
 * Free text is capped, not filtered. The cap is a payload bound (a runaway
 * client cannot post a novel), not a privacy control — the privacy treatment
 * is that these words never leave India and never reach the crash sink.
 */
export const MAX_REPORT_TEXT_LENGTH = 4000;

/**
 * The short reference the reporter is shown at submit and quotes back later
 * ("reference: 4F2A-91C7"). Derived, never stored.
 *
 * Taken from the id's RANDOM tail rather than its head: a UUIDv7's leading
 * bytes are a millisecond timestamp, so two reports filed in the same session
 * would share a head-derived reference almost exactly — which is the one thing
 * a reference must never do. The tail is random by construction.
 *
 * Case-folded and grouped for reading aloud over a phone line, which is how
 * these will actually be quoted.
 */
export function reportReference(reportId: string): string {
  const tail = reportId.replace(/-/g, '').slice(-8).toUpperCase();
  return `${tail.slice(0, 4)}-${tail.slice(4)}`;
}

/**
 * One breadcrumb — the shape of an API call or a screen change, never its
 * content. `pathTemplate` is the ROUTE (`/api/sessions/:id/taps`), never the
 * concrete path: a concrete path carries record ids, and M1.3 keeps those out
 * of anything that can travel. The scrubber enforces this independently; the
 * schema states it so a client cannot claim it did not know.
 */
export const reportBreadcrumbSchema = z.object({
  kind: z.enum(['screen', 'api', 'crash']),
  /** Screen NAME, or the route template — never a concrete path (M1.3). */
  screen: z.string().max(120).optional(),
  method: z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']).optional(),
  pathTemplate: z.string().max(200).optional(),
  status: z.number().int().min(100).max(599).optional(),
  requestId: z.string().max(64).optional(),
  at: z.iso.datetime(),
});
export type ReportBreadcrumb = z.infer<typeof reportBreadcrumbSchema>;

/**
 * The diagnostics block (M2.3). Every field here is already named in the
 * scrubber's `report` allow-list — this schema is the client-side statement of
 * the same contract, so a malformed block is refused at the edge with a
 * message instead of silently arriving with holes in it.
 *
 * Deliberately `.loose()` on the outer object: the block WILL grow (a new
 * pipeline number, a new permission), and a strict schema would make every
 * addition a coordinated release across app and API. What must never grow is
 * what is ALLOWED to survive, and that is the scrubber's job, applied
 * server-side before the INSERT — one gate, in the one place that decides.
 */
export const reportDiagnosticsSchema = z
  .object({
    release: z.string().max(120).optional(),
    instance: z.string().max(32).optional(),
    appRunId: z.uuid().optional(),
    device: z.string().max(120).optional(),
    os: z.string().max(60).optional(),
    osVersion: z.string().max(60).optional(),
    screen: z.string().max(120).optional(),
    networkState: z.string().max(40).optional(),
    inCall: z.boolean().optional(),
    signedIn: z.boolean().optional(),
    clockOffsetMs: z.number().optional(),
    freeStorageBytes: z.number().nonnegative().optional(),
    uploadQueueDepth: z.number().int().nonnegative().optional(),
    pendingParts: z.number().int().nonnegative().optional(),
    breadcrumbs: z.array(reportBreadcrumbSchema).max(50).optional(),
  })
  .loose();
export type ReportDiagnostics = z.infer<typeof reportDiagnosticsSchema>;

/**
 * POST /api/reports — one report, as the device recorded it.
 *
 * `sessionId` is optional and OWNED: absent for a report raised from the
 * Account tab, present for one raised mid-call, and when present the route
 * proves the session is the reporter's own (ADR-0017) before anything is
 * written. The schema cannot express ownership; it only says the shape.
 */
export const submitReportRequestSchema = z.object({
  /** Device-minted UUIDv7 — the idempotency key for offline replay. */
  id: z.uuid(),
  sessionId: z.uuid().optional(),
  category: z.enum(REPORT_CATEGORIES),
  severity: z.enum(REPORT_SEVERITIES),
  /** The person's own words. Absent is legal — a one-tap report is a report. */
  freeText: z.string().max(MAX_REPORT_TEXT_LENGTH).optional(),
  diagnostics: reportDiagnosticsSchema,
  /** Sentry event ids this report points at (M2.7). */
  crashEventIds: z.array(z.string().max(64)).max(20).optional(),
  /** The reporter's clock at submit — never rewritten by sync lag. */
  occurredAt: z.iso.datetime(),
});
export type SubmitReportRequest = z.infer<typeof submitReportRequestSchema>;

export const submitReportResponseSchema = z.object({
  ok: z.literal(true),
  reportId: z.uuid(),
  /** What the reporter is shown and quotes back (`reportReference`). */
  reference: z.string(),
  /**
   * False when this id was already in the ledger — the idempotent replay case.
   * The queue treats both as settled; the distinction exists so a client can
   * tell "delivered now" from "delivered before" in its own logs without
   * guessing from a status code.
   */
  recorded: z.boolean(),
});
export type SubmitReportResponse = z.infer<typeof submitReportResponseSchema>;

/**
 * The reader's rule for a report's CURRENT status: the latest event by the
 * clock the operator acted on, ties broken by server receipt. Mirrors how
 * `capture_artifact_events` resolves upload state — latest event wins, and
 * nothing is ever rewritten to make it so.
 *
 * Returns null for a report with no events at all, which the submit path makes
 * impossible (it appends `new` in the same transaction) but which a reader
 * must not assume away — a hand-inserted row from the runbook could lack one.
 */
export function currentReportStatus(
  events: ReadonlyArray<{ status: ReportStatus; occurredAt: string; recordedAt: string }>,
): ReportStatus | null {
  let latest: { status: ReportStatus; occurredAt: string; recordedAt: string } | null = null;
  for (const event of events) {
    if (
      latest == null ||
      event.occurredAt > latest.occurredAt ||
      (event.occurredAt === latest.occurredAt && event.recordedAt > latest.recordedAt)
    ) {
      latest = event;
    }
  }
  return latest?.status ?? null;
}
